import React from 'react';
import i18n from '@fiverr/i18n/singleton';
import { Section } from '@fiverr-private/fit';
import { I18nProvider, hasTranslation } from '@fiverr-private/i18n-react';
import { getContext } from '@fiverr-private/fiverr_context';
import { getBuyerAssumedLanguage } from '@fiverr-private/localization';
import { RelatedGuides, RelatedLinks } from '@fiverr-private/seo_content';
import Faqs from '../Faqs';
import { HOW_FIVERR_BUSINESS_WORKS, HOW_FIVERR_WORKS } from '../video/constants';
import eventReporter from '../../utils/reporter';
import { SCROLL_TYPE } from '../carousel/constants';
import { BI_CONSTS } from '../../constants';
import { EVENT_TYPES } from '../../utils/reporter/constants';
import MostPopularSlide from '../MostPopularSlide';
import MostPopularHeader from '../MostPopularHeader';
import BucketsContainer from '../BucketsContainer';
import HeroBanner from '../HeroBanner';
import { BannerSection } from '../BannerSection/BannerSection';
import { createSourceInfo } from '../utils';
import Carousel from '../carousel';

import './index.scss';

interface VerticalPageProps {
    verticalData: IVertical;
    isBusiness: boolean;
}

const VerticalPage = ({ verticalData, isBusiness }: VerticalPageProps) => {
    const { locale, browserLanguage, countryCode } = getContext();
    const assumedLanguage = getBuyerAssumedLanguage({
        locale,
        browserLanguage,
        countryCode,
    });
    const {
        id: verticalId,
        title: verticalTitle,
        mostPopularLinks: { title: mostPopularTitle, items },
    } = verticalData;
    const bannerTitle =
        verticalData.seoData?.localizedHeading ||
        i18n.t(`categories.category_${verticalId}.name`, {
            $scope: locale,
        });

    const video = isBusiness ? HOW_FIVERR_BUSINESS_WORKS : HOW_FIVERR_WORKS;

    const sourceInfo = createSourceInfo({
        videoName: `${video.name} - ${assumedLanguage}`,
        mixpanelPageName: BI_CONSTS.mixpanel.pageName,
        bigQueryPageName: BI_CONSTS.bigQuery.pageName,
    });

    const clickOnHowFiverrWorks = () =>
        eventReporter.reportEvent(EVENT_TYPES.CLICKED_HOW_FIVERR_WORKS, {
            categoryId: verticalId,
            categoryName: bannerTitle,
        });

    return (
        <div className="vertical-page">
            <Section equalPadding className="banner-section">
                <HeroBanner
                    verticalData={verticalData}
                    video={video}
                    title={bannerTitle}
                    sourceInfo={sourceInfo}
                    clickOnHowFiverrWorks={clickOnHowFiverrWorks}
                />
            </Section>

            <Section className="carousel-section">
                <Carousel
                    slides={items}
                    dynamicSlidesWidth
                    Header={<MostPopularHeader title={mostPopularTitle} />}
                    scrollType={SCROLL_TYPE.ROW}
                    componentProps={verticalData}
                    carouselClassname="most-popular-carousel"
                    Component={MostPopularSlide}
                />
            </Section>

            <BannerSection verticalId={verticalId} verticalTitle={verticalTitle} />

            <Section className="buckets-section" equalPadding>
                <BucketsContainer verticalData={verticalData} />
            </Section>

            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore  */}
            <I18nProvider>
                {({ i18n }) => {
                    const { seoData: { relatedLinks = [], relatedGuides = [], faqs: builderFaqs } = {}, cachedSlug } =
                        verticalData;

                    const relatedGuidesSourcePageKey = `verticals_${cachedSlug}`;

                    const faqsTranslationKey = `seo_categories.${verticalData.id}.faqs`;
                    const faqs =
                        builderFaqs?.items || (hasTranslation(faqsTranslationKey) ? i18n.t(faqsTranslationKey) : []);

                    const mappedRelatedGuides = relatedGuides?.map((guide) => ({
                        guideTitle: guide.title,
                        guideUrl: guide.article_link,
                        imageUrl: guide.image_link,
                        imageAlt: guide.image_alt,
                    }));

                    const mappedRelatedLinks = relatedLinks?.map((link) => ({
                        text: link.anchor_text,
                        url: link.url,
                        locale: link.locale,
                    }));

                    return (
                        <>
                            {mappedRelatedGuides.length > 0 && (
                                <Section className="related-guides-section">
                                    <RelatedGuides
                                        guides={{ items: mappedRelatedGuides }}
                                        title={i18n.t('vertical_page_perseus.seo_sub_category.related_guides.title', {
                                            sub_category_title: verticalData.title,
                                        })}
                                        sourcePageKey={relatedGuidesSourcePageKey}
                                    />
                                </Section>
                            )}
                            {faqs.length > 0 && (
                                <Faqs
                                    title={i18n.t('vertical_page_perseus.seo_sub_category.faqs.title', {
                                        sub_category_title: verticalData.title,
                                    })}
                                    faqs={faqs}
                                    verticalData={verticalData}
                                />
                            )}
                            {mappedRelatedLinks.length > 0 && (
                                <RelatedLinks
                                    relatedLinks={{ items: mappedRelatedLinks }}
                                    title={i18n.t('vertical_page_perseus.seo_sub_category.relatedLinks.title', {
                                        sub_category_title: verticalData.title,
                                    })}
                                    isVerticalPage
                                />
                            )}
                        </>
                    );
                }}
            </I18nProvider>
        </div>
    );
};

export default VerticalPage;
